import React, { useEffect, useState } from "react";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import useFirebaseInit from "../firebase/useFirebaseInit";  // Import the updated hook
import { useLoginFirebase } from "../../hooks/dataHooks";
import { Button, CircularProgress, Typography, Box, Paper } from "@mui/material";
import googleLogo from "../../assets/images/google-logo.png";

const GoogleLogin = ({ onClose }) => {
    const [token, setToken] = useState("");
    const [error, setError] = useState(null);
    const { mutate: loginFirebase, isPending: isLoggingIn } = useLoginFirebase({ onSuccessCallback: onClose });

    // Use the custom hook to initialize Firebase
    const { firebaseAuth, isFirebaseConfigPending, isFirebaseConfigError, fetchFirebaseConfig } = useFirebaseInit();

    useEffect(() => {
        if (token && loginFirebase) {
            loginFirebase({ token });
        }
    }, [token, loginFirebase]);

    const handleGoogleLogin = async () => {
        if (isFirebaseConfigPending) {
            return; // Wait for Firebase initialization
        }
        if (isFirebaseConfigError) {
            setError("Failed to load Firebase config. Please check your internet connection or try again.");
            return;
        }

        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(firebaseAuth, provider);
            const token = await result.user.getIdToken();
            setToken(token);
            setError(null);
        } catch (error) {
            console.error("Error during login:", error.message);
            setError("Failed to sign in. Please try again.");
        }
    };

    const handleRetry = () => {
        fetchFirebaseConfig();  // Retry fetching the Firebase config
    };

    // Show loading state until Firebase is initialized
    if (isFirebaseConfigPending) {
        return <CircularProgress />;
    }

    // If there is an error initializing Firebase
    if (isFirebaseConfigError) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%" bgcolor="background.paper">
                <Paper elevation={3} sx={{ p: 4, maxWidth: 400, textAlign: "center", borderRadius: 2 }}>
                    <Typography variant="h6" color="error" gutterBottom>
                        Firebase Initialization Failed
                    </Typography>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                        We encountered an issue while loading the Firebase configuration. Please try again later or check your connection.
                    </Typography>
                    <Button variant="contained" color="primary" onClick={handleRetry} sx={{ mt: 2 }}>
                        Retry
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%" width="100%">
            <Paper elevation={3} sx={{ p: 4, maxWidth: 400, textAlign: "center", borderRadius: 2 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Welcome to Glassyfied
                </Typography>
                <Typography variant="body1" color="textSecondary" mb={2}>
                    Sign in to continue
                </Typography>
                {error && (
                    <Typography variant="body2" color="error" sx={{ mb: 2, textAlign: "left" }}>
                        {error}
                    </Typography>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGoogleLogin}
                    disabled={isLoggingIn}
                    fullWidth
                    startIcon={isLoggingIn ? <CircularProgress size={20} /> : <img src={googleLogo} alt="Google logo" width="24" />}
                    sx={{ textTransform: "none", fontWeight: "bold", fontSize: 16, padding: "12px", boxShadow: "0 4px 10px rgba(0,0,0,0.1)" }}
                >
                    {isLoggingIn ? "Signing in..." : "Sign in with Google"}
                </Button>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    By signing in, you agree to our{" "}
                    <Box component="a" href="/terms" color="primary.main" sx={{ textDecoration: "none" }}>
                        Terms
                    </Box>{" "}
                    and{" "}
                    <Box component="a" href="/privacy" color="primary.main" sx={{ textDecoration: "none" }}>
                        Privacy Policy
                    </Box>
                    .
                </Typography>
            </Paper>
        </Box>
    );
};

export default GoogleLogin;
